import { trackFormError } from "utils/analytics/trackEvent";
import { useCallback, useEffect, useState } from "react";
import {
  useCreateHealthApplicationMutation,
  useGetHealthAppStepQuery,
} from "types/generated/operations";
import Error, { EErrorType } from "uicomponents/Error";
import Loader from "uicomponents/Loader";
import {
  getProspectCanonicalId,
  getProspectUuid,
  getMemberUuid,
} from "utils/idHelpers";
import { getAppType, hasQueryParam } from "utils/helper";
import HealthInsuranceApplication from "./NewHealthInsuranceApp";
import CoreAppWithHI from "./CoreAppWithHI";
import CoreAppWithoutHI from "./CoreAppWithoutHI";
import { sendCustomerIOEvent } from "utils/analytics/customerio";
import { useOktaAuth } from "@okta/okta-react";
import { getLaunchDarklyFlag } from "app/analytics/launch-darkly";
import {
  HEALTH_INSURANCE_STEPS,
  HEALTH_INSURANCE_STEPS_WITH_CORE_APP_OFFER_HI,
  HEALTH_INSURANCE_STEPS_WITH_CORE_APP_WITHOUT_HI,
} from "utils/constants";
import { generateAppStepUrl } from "utils/stepApiHelpers";
import useFetch from "utils/useFetch";
import { postApplicationStepUpdate } from "utils/stepApiHelpers";

const HealthInsuranceApplicationModel = () => {
  const appType = getAppType();

  const [isNewApp, setIsNewApp] = useState(false);
  const stepUrl = generateAppStepUrl();

  const { data, error, loading, refetch } = useGetHealthAppStepQuery({
    variables: { uuid: getProspectUuid(), appType: appType },
    fetchPolicy: "no-cache",
    skip: !getProspectUuid(),
  });

  const [createHealthApplication] = useCreateHealthApplicationMutation();

  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken;

  const {
    data: stepData,
    refetch: refetchSteps,
  }: {
    data: any;
    error: any;
    loading: boolean;
    refetch: () => void;
  } = useFetch(stepUrl, {
    authToken: authState?.accessToken?.accessToken,
  });

  const useNewSteps = getLaunchDarklyFlag("cml-use-new-steps");
  const isInterestedInHI =
    data?.prospect?.healthInsuranceInformation?.currentlyOfferHealthInsurance;
  const hi_flow_with_core_app = hasQueryParam("usecoreapp");
  const coreAppWithHI = hi_flow_with_core_app && isInterestedInHI;
  const coreAppWithoutHI = hi_flow_with_core_app && !isInterestedInHI;

  const createNewApplication = useCallback(async () => {
    try {
      const getHealthAppSteps = () => {
        if (coreAppWithHI) {
          return HEALTH_INSURANCE_STEPS_WITH_CORE_APP_OFFER_HI;
        } else if (coreAppWithoutHI) {
          return HEALTH_INSURANCE_STEPS_WITH_CORE_APP_WITHOUT_HI;
        } else if (useNewSteps) {
          return HEALTH_INSURANCE_STEPS;
        } else {
          return [];
        }
      };
      setIsNewApp(true);
      await createHealthApplication({
        variables: {
          prospectCanonicalId: getProspectCanonicalId(),
          prospectId: getProspectUuid(),
          appType: appType,
          memberUuid: getMemberUuid(),
          applicationSteps: getHealthAppSteps(),
        },
      });

      if (useNewSteps || hi_flow_with_core_app) {
        await postApplicationStepUpdate({
          stepName: "start_application",
          completed: true,
          token: accessToken || "",
        });
      }

      sendCustomerIOEvent(
        {
          event_name: "health_insurance_application_started",
          event_type: "health_app_stages",
          member_uuid: getMemberUuid(),
          data: {
            health_app_status: "HI_STARTED",
            app_name: appType,
            version: 1, // TODO: use flowVersion
          },
        },
        accessToken || ""
      );

      refetch();
      refetchSteps();
    } catch (error: any) {
      trackFormError(error, `error creating ${appType}`);

      return <Error />;
    }
  }, [
    createHealthApplication,
    refetch,
    appType,
    refetchSteps,
    useNewSteps,
    accessToken,
    hi_flow_with_core_app,
    coreAppWithHI,
    coreAppWithoutHI,
  ]);

  useEffect(() => {
    if (!data?.prospect?.applications?.length && !loading && !error) {
      // if a prospect does not have an application, create one
      createNewApplication();
    }
  }, [data, error, loading, createNewApplication]);

  if (error) {
    trackFormError(error, `error getting ${appType} status`);
    return <Error />;
  }

  if (
    data?.prospect?.healthInsuranceInformation?.isCoverageEligible === false
  ) {
    return <Error type={EErrorType.NotEligibleForCoverage} />;
  }

  const renderHealthInsuranceApp = () => {
    if (coreAppWithHI) {
      return (
        <CoreAppWithHI
          data={data?.prospect}
          refetchStepData={refetch}
          stepData={stepData}
          refetchSteps={refetchSteps}
          disableNextStep={loading}
        />
      );
    }

    if (coreAppWithoutHI) {
      return (
        <CoreAppWithoutHI
          data={data?.prospect}
          refetchStepData={refetch}
          stepData={stepData}
          refetchSteps={refetchSteps}
          disableNextStep={loading}
        />
      );
    }

    return (
      <HealthInsuranceApplication
        data={data?.prospect}
        refetchStepData={refetch}
        stepData={stepData}
        refetchSteps={refetchSteps}
        disableNextStep={loading}
      />
    );
  };

  return data?.prospect?.applications?.length || isNewApp ? (
    renderHealthInsuranceApp()
  ) : (
    <Loader />
  );
};

export default HealthInsuranceApplicationModel;
